import { useSelector } from '@services/hooks/redux-hooks';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import EsfirusSelect from '@ui/Select/EsfirusSelect';
import DatePickerUI from '../../../../../components/ui/DatePickerUI/DatePickerUI';
import { getAllCompaniesData, getUserId, isAdmin, isJefeObra, isSupervisor } from '@services/hooks/UserHook';
import { TIPOS_ESTADOS_PEDIDOS } from '../../constants';
import { sortEmpleados } from '@helpers/utils';
import "./ListadoPedidosFilters.scss";

const ListadoPedidosFilters = ({ filters, setFilters, items, availableFilters }: any) => {
  const empresas = useSelector(state => state.empresas.list)
  const users = useSelector(state => state.users.list)
  const obras = useSelector(state => state.obras.fullList)
  const userCanSupervise = isAdmin() || isSupervisor()
  const userIsJefeObra = isJefeObra()

  const empleadosList = useMemo(() => {
    if (!items.length) return [];

    return sortEmpleados(users).reduce((acc: { label: string; value: string | number }[], empleado) => {
      const { internal = [], external = [] } = empleado.data;
      const idsEmpresas = [...internal, ...external].map((e) => Number(e.empresa));
      const filteredItems = (availableFilters?.responsible_employees || []).filter(Boolean);

      if (idsEmpresas.includes(+filters.empresa) || !filters.empresa) {
        const idEmpresaInterna = empleado.data.internal[0].empresa;
        const empresaObject = empresas.find(
          (empresa) => empresa.Empresa === Number(idEmpresaInterna),
        );

        if (filteredItems.includes(String(empleado.data.internal[0].empleado))) {
          const label = `${empleado.nombre} ${empleado.apellidos} - ${empresaObject?.Nombre ?? '-'}`;
          acc.push({
            label,
            value: empleado.id,
          });
        }
      }
      return acc;
    }, []);
  }, [users, filters.empresa, empresas, items]);

  const companyList = useMemo(() => {
    const filteredItems = new Set(items.map((item: any) => Number(item.company_r_403)));

    return empresas
      .filter((e: any) => filteredItems.has(Number(e.Empresa)))
      .map((e: any) => ({ label: e.Nombre, value: e.Empresa }));
  }, [items, empresas]);

  // Opciones del filtro de estado
  const statusOptions = useMemo(() => {
    const filteredItems = availableFilters?.statuses || [];

    if (filteredItems.includes(TIPOS_ESTADOS_PEDIDOS.ABIERTO.value) && filteredItems.includes(TIPOS_ESTADOS_PEDIDOS.CONFIRMADO.value)) {
      filteredItems.push(TIPOS_ESTADOS_PEDIDOS.ABIERTO_CONFIRMADO.value);
    }

    return Object.values(TIPOS_ESTADOS_PEDIDOS).filter(status => filteredItems.includes(status.value));
  }, [availableFilters]);

  // Opciones de obra
  const obrasOptions = useMemo(() => {
    if (!userIsJefeObra) return []

    const allCompaniesData = getAllCompaniesData()
    const obrasResponsable = obras.filter(o => allCompaniesData.some(c => c.empresa === o.Empresa && c.empleado === o.Responsable))
    const options = obrasResponsable.map(o => ({ label: o.Descripcion ?? "-", value: o.Obra }))
    return options
  }, [userIsJefeObra])

  useEffect(() => {
    if (!userCanSupervise) {
      const internalEmployeeId = String(getUserId());
      if (filters.empleado !== internalEmployeeId) {
        setFilters((prevFilters: any) => ({ ...prevFilters, empleado: internalEmployeeId }))
      }
    }
  }, [empresas]);

  return (
    <div className='ef-pedidos-filtros'>
      <div className="ef-left">
        {/* Filtro de empresa */}
        {userCanSupervise && (
          <EsfirusSelect
            placeholder='Seleccionar Empresa'
            options={companyList}
            value={filters.empresa}
            change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, empresa: e }))}
            fill={!!filters.empresa}
          />
        )}

        {/* Filtro de fecha inicio */}
        <DatePickerUI
          className='datePickerFilter'
          value={filters.dateFrom}
          handleChange={newDate => {
            const dateSelected = moment({ day: newDate.$D, month: newDate.$M, year: newDate.$y })
            const isCorrect = dateSelected.isSameOrBefore(filters.dateTo)
            if (isCorrect) setFilters((prevFilters: any) => ({ ...prevFilters, dateFrom: dateSelected }))
          }}
          fill={!!filters.dateFrom}
        />

        {/* Filtro de fecha fin */}
        <DatePickerUI
          className='datePickerFilter'
          value={filters.dateTo}
          handleChange={newDate => {
            const dateSelected = moment({ day: newDate.$D, month: newDate.$M, year: newDate.$y })
            const isCorrect = dateSelected.isSameOrAfter(filters.dateFrom)
            if (isCorrect) setFilters((prevFilters: any) => ({ ...prevFilters, dateTo: dateSelected }))
          }}
          fill={!!filters.dateTo}
        />

        {/* Filtro de obras */}
        {userIsJefeObra && (
          <EsfirusSelect
            options={obrasOptions}
            value={filters.obra}
            change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, obra: e }))}
            placeholder="Selecciona obra"
            // disableBlank={true}
            fill={!!filters.obra}
          />
        )}

        {/* Filtro de empleado */}
        {userCanSupervise && (
          <EsfirusSelect
            options={empleadosList}
            value={filters.empleado}
            change={(e: any) => setFilters((prevFilters: any) => ({ ...prevFilters, empleado: e }))}
            placeholder="Todos los Empleados"
            fill={!!filters.empleado}
          />
        )}

        {/* Filtro de estado */}
        <EsfirusSelect
          options={statusOptions}
          value={filters.status}
          change={(e: any) => {
            const excludeStatus = statusOptions.find(option => option.value === e)?.excludeStatus ?? false;
            setFilters((prevFilters: any) => ({
              ...prevFilters,
              status: e,
              excludeStatus
            }));
          }}
          placeholder="Todos los Estados"
        />
      </div>
    </div>
  );
}

export default ListadoPedidosFilters;